import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '@/lib/api';
import { BULLETIN } from '@/constants/testIds';
import { trackPageView } from '@/lib/attribution';
import { ExternalLink, Flame, MessageSquare, Newspaper, Rss, TrendingUp } from 'lucide-react';

const SOURCE_META = {
  hn:     { label: 'HN',     color: '#FF6600', icon: TrendingUp },
  reddit: { label: 'REDDIT', color: '#FF4500', icon: Newspaper },
  rss:    { label: 'RSS',    color: '#38BDF8', icon: Rss },
};

const timeAgo = (iso) => {
  if (!iso) return '—';
  try {
    const diff = Math.max(0, Date.now() - new Date(iso).getTime());
    const mins = Math.floor(diff / 60000);
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    return `${Math.floor(hrs / 24)}d ago`;
  } catch { return iso; }
};

export const BulletinPage = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    trackPageView('bulletin');
    (async () => {
      try {
        const { data } = await api.get('/bulletin');
        setData(data);
      } catch { /* silent */ }
      finally { setLoading(false); }
    })();
  }, []);

  return (
    <div data-testid={BULLETIN.container} className="max-w-4xl mx-auto px-6 md:px-8 pt-10 pb-24">
      <div className="text-[10px] tracking-[0.4em] uppercase text-[#38BDF8] mb-3">
        // WEEKLY_OSINT_BULLETIN · SYSTEM_WIDE_AGGREGATE
      </div>
      <h1 className="text-3xl sm:text-4xl font-bold uppercase tracking-tighter mb-4">
        This Week in OSINT<span className="caelix-caret"></span>
      </h1>
      <p className="text-xs text-gray-500 mb-8 max-w-2xl leading-relaxed">
        &gt; The top signals across every Caelix operator&apos;s saved queries this week —
        anonymously aggregated, ranked by how many operators independently surfaced them.
        Zero attribution, zero PII. Fresh every 30 minutes.
      </p>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
        <div className="c-card">
          <div className="c-label">LOOKBACK</div>
          <div className="text-3xl font-bold text-white">
            {data?.lookback_days ?? 7}<span className="text-sm text-gray-500 ml-1">D</span>
          </div>
        </div>
        <div className="c-card">
          <div className="c-label">TOTAL_OPERATORS</div>
          <div
            data-testid={BULLETIN.totalOperators}
            className="text-3xl font-bold text-white"
          >
            {String(data?.total_operators ?? 0).padStart(3, '0')}
          </div>
        </div>
        <div className="c-card">
          <div className="c-label">SIGNALS_INGESTED</div>
          <div
            data-testid={BULLETIN.totalSignals}
            className="text-3xl font-bold text-[#38BDF8]"
          >
            {String(data?.total_signals_last_week ?? 0).padStart(3, '0')}
          </div>
        </div>
      </div>

      {loading ? (
        <div className="c-card text-xs text-gray-500 tracking-widest uppercase">
          AGGREGATING<span className="caelix-dots"></span>
        </div>
      ) : !data || data.entries.length === 0 ? (
        <div data-testid={BULLETIN.emptyState} className="c-card">
          <div className="text-xs text-gray-500 tracking-widest uppercase">
            &gt; not enough data yet — check back after some operators save queries
          </div>
        </div>
      ) : (
        <div className="c-card">
          <div className="border-b border-[#3F3F46] pb-3 mb-4 flex items-center justify-between">
            <div className="text-[10px] tracking-[0.3em] uppercase text-gray-500">
              // TOP_{data.entries.length} · SYSTEM_TRENDING
            </div>
            <div className="text-[10px] tracking-widest uppercase text-gray-600">
              REFRESH :: {timeAgo(data.generated_at)}
            </div>
          </div>
          <div className="space-y-2">
            {data.entries.map((e) => {
              const sm = SOURCE_META[e.source] || SOURCE_META.hn;
              const SIcon = sm.icon;
              return (
                <a
                  key={`${e.source}-${e.rank}`}
                  data-testid={BULLETIN.row}
                  href={e.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block border border-[#3F3F46] bg-black p-4 hover:border-[#38BDF8]/50 transition-colors group"
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <span className="font-mono text-[#38BDF8] text-lg font-bold">
                          {String(e.rank).padStart(2, '0')}
                        </span>
                        <SIcon className="w-3 h-3" strokeWidth={2.5} style={{ color: sm.color }} />
                        <span className="text-[10px] tracking-[0.2em] uppercase" style={{ color: sm.color }}>
                          {sm.label}
                        </span>
                        <span className="inline-flex items-center gap-1 text-[10px] tracking-widest uppercase text-yellow-400">
                          <Flame className="w-3 h-3" strokeWidth={2.5} /> REACH {e.reach}
                        </span>
                      </div>
                      <div className="text-sm text-white group-hover:text-[#38BDF8] transition-colors">
                        {e.title}
                      </div>
                      <div className="mt-2 flex items-center gap-4 text-[10px] tracking-widest uppercase text-gray-600 font-mono">
                        {typeof e.score === 'number' && (
                          <span className="inline-flex items-center gap-1">
                            <TrendingUp className="w-3 h-3" strokeWidth={2.5} /> {e.score}
                          </span>
                        )}
                        {typeof e.comments === 'number' && (
                          <span className="inline-flex items-center gap-1">
                            <MessageSquare className="w-3 h-3" strokeWidth={2.5} /> {e.comments}
                          </span>
                        )}
                        <span>{timeAgo(e.posted_at || e.ingested_at)}</span>
                      </div>
                    </div>
                    <ExternalLink className="w-4 h-4 text-gray-600 group-hover:text-[#38BDF8] transition-colors shrink-0 mt-1" strokeWidth={2.5} />
                  </div>
                </a>
              );
            })}
          </div>
        </div>
      )}

      <div className="mt-8 c-card">
        <div className="text-[10px] tracking-[0.3em] uppercase text-gray-500 mb-3">
          // WANT_YOUR_OWN_FEED?
        </div>
        <p className="text-xs text-gray-500 mb-4 leading-relaxed">
          The Bulletin is public. Your <span className="text-[#38BDF8]">private</span> Signal Feed lets you save unlimited keyword queries across HackerNews, Reddit, and custom RSS — all filtered to what YOU care about.
        </p>
        <div className="flex flex-wrap gap-3">
          <Link data-testid={BULLETIN.cta} to="/signup" className="c-btn-primary text-xs">CREATE_OPERATOR</Link>
          <Link to="/pricing" className="c-btn-secondary text-xs">VIEW_PRICING</Link>
          <Link to="/leaderboard" className="c-btn-secondary text-xs">SEE_LEADERBOARD</Link>
        </div>
      </div>

      <div className="mt-6 text-[10px] text-gray-600 tracking-widest uppercase text-center">
        &gt; regenerated every 30 min · next refresh {data?.expires_at ? timeAgo(data.expires_at) : '—'}
      </div>
    </div>
  );
};

export default BulletinPage;
